import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { listUsers } from '@/features/auth/api'
import { createRule, updateRule, type CommissionRule, type RuleInput } from '@/features/commissions/api'
import { flattenCategories, listCategories } from '@/features/inventory/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

/**
 * Create and edit in one dialog, the same split as `UserFormDialog`.
 *
 * A rule is read as a whole -- kind, rate and tiers only mean anything together -- so this
 * sends a full `RuleInput` on every save, matching `RuleUpdate` on the API: there is no
 * partial-patch path to fall back on here.
 */

const tierSchema = z.object({
  min_amount: z
    .string()
    .trim()
    .refine((v) => v !== '' && Number.isFinite(Number(v)) && Number(v) >= 0, {
      message: 'Enter a non-negative amount',
    }),
  rate_pct: z
    .string()
    .trim()
    .refine((v) => v !== '' && Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100, {
      message: 'Enter a rate between 0 and 100',
    }),
})

const schema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name').max(100, 'That name is too long'),
    kind: z.enum(['flat', 'tiered']),
    user_id: z.string(),
    category_id: z.string(),
    rate_pct: z.string().trim(),
    tiers: z.array(tierSchema),
    valid_from: z.string().min(1, 'Pick a start date'),
    valid_to: z.string(),
    is_active: z.boolean(),
    sequence: z
      .string()
      .trim()
      .refine((v) => v === '' || (Number.isInteger(Number(v)) && Number(v) >= 0), {
        message: 'Enter a whole number, 0 or more',
      }),
  })
  .superRefine((values, ctx) => {
    if (values.kind === 'flat') {
      const rate = Number(values.rate_pct)
      if (values.rate_pct === '' || !Number.isFinite(rate) || rate < 0 || rate > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rate_pct'],
          message: 'A flat rule needs a rate between 0 and 100',
        })
      }
    } else if (values.tiers.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tiers'],
        message: 'A tiered rule needs at least one tier',
      })
    }
    if (values.valid_to && values.valid_to < values.valid_from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valid_to'],
        message: 'This cannot come before the start date',
      })
    }
  })

type FormValues = z.infer<typeof schema>

export function RuleFormDialog({
  open,
  rule,
  onClose,
}: {
  open: boolean
  /** Absent for a new rule. */
  rule?: CommissionRule
  onClose: () => void
}) {
  const isEdit = rule !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const users = useQuery({ queryKey: ['users', 1], queryFn: () => listUsers(1), retry: false })
  const categories = useQuery({ queryKey: ['categories'], queryFn: listCategories, retry: false })
  const categoryOptions = flattenCategories(categories.data ?? [])

  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: rule?.name ?? '',
      kind: rule?.kind ?? 'flat',
      user_id: rule?.user_id ?? '',
      category_id: rule?.category_id ?? '',
      rate_pct: rule?.rate_pct ?? '',
      tiers: rule?.tiers.map((tier) => ({ min_amount: tier.min_amount, rate_pct: tier.rate_pct })) ?? [],
      valid_from: rule?.valid_from ?? '',
      valid_to: rule?.valid_to ?? '',
      is_active: rule?.is_active ?? true,
      sequence: String(rule?.sequence ?? 100),
    },
  })

  const kind = watch('kind')
  const { fields, append, remove } = useFieldArray({ control, name: 'tiers' })

  // Switching to "tiered" with nothing entered yet would show an empty table and a top-level
  // "add at least one tier" error with no row to fix it on -- so the first row is there
  // the moment the shape needs one.
  useEffect(() => {
    if (kind === 'tiered' && fields.length === 0) append({ min_amount: '', rate_pct: '' })
  }, [kind, fields.length, append])

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const input: RuleInput = {
        name: values.name,
        kind: values.kind,
        user_id: values.user_id || null,
        category_id: values.category_id || null,
        rate_pct: values.kind === 'flat' ? values.rate_pct : null,
        tiers: values.kind === 'tiered' ? values.tiers : [],
        valid_from: values.valid_from,
        valid_to: values.valid_to || null,
        is_active: values.is_active,
        sequence: values.sequence === '' ? 100 : Number(values.sequence),
      }
      return rule ? updateRule(rule.id, input) : createRule(input)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['commission-rules'] })
      toast.success(isEdit ? `Saved ${saved.name}.` : `Created ${saved.name}.`)
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${rule.name}` : 'New rate card'}
        description="A rule with no rep and no category is the company default. The most specific matching rule wins; sequence breaks a tie."
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="rule-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Create rule'}
            </Button>
          </>
        }
      >
        <form id="rule-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="Name" error={errors.name?.message}>
            {(props) => (
              <Input {...props} {...register('name')} autoComplete="off" placeholder="Festive season" />
            )}
          </Field>

          <Field label="Kind" error={errors.kind?.message}>
            {(props) => (
              <select
                {...props}
                {...register('kind')}
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="flat">Flat rate</option>
                <option value="tiered">Tiered</option>
              </select>
            )}
          </Field>

          {kind === 'flat' ? (
            <Field label="Rate (%)" error={errors.rate_pct?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('rate_pct')}
                  inputMode="decimal"
                  autoComplete="off"
                  className="tabular-nums"
                />
              )}
            </Field>
          ) : (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Tiers
              </legend>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                The whole basis earns the highest rate it reaches -- each row is "from this
                amount", not a range.
              </p>
              {errors.tiers?.message && (
                <p role="alert" className="text-sm text-danger">
                  {errors.tiers.message}
                </p>
              )}
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-end gap-2">
                    <Field
                      label="From amount"
                      error={errors.tiers?.[index]?.min_amount?.message}
                    >
                      {(props) => (
                        <Input
                          {...props}
                          {...register(`tiers.${index}.min_amount`)}
                          inputMode="decimal"
                          autoComplete="off"
                          className="tabular-nums"
                        />
                      )}
                    </Field>
                    <Field label="Rate (%)" error={errors.tiers?.[index]?.rate_pct?.message}>
                      {(props) => (
                        <Input
                          {...props}
                          {...register(`tiers.${index}.rate_pct`)}
                          inputMode="decimal"
                          autoComplete="off"
                          className="tabular-nums"
                        />
                      )}
                    </Field>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                      aria-label="Remove tier"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => append({ min_amount: '', rate_pct: '' })}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add tier
              </Button>
            </fieldset>
          )}

          <Field
            label="Applies to rep"
            error={errors.user_id?.message}
            hint="Leave blank to apply to every rep."
          >
            {(props) => (
              <select
                {...props}
                {...register('user_id')}
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="">Every rep</option>
                {(users.data?.data ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            label="Applies to category"
            error={errors.category_id?.message}
            hint="Leave blank to apply to every category."
          >
            {(props) => (
              <select
                {...props}
                {...register('category_id')}
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="">Every category</option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Valid from" error={errors.valid_from?.message}>
              {(props) => <Input {...props} {...register('valid_from')} type="date" />}
            </Field>
            <Field label="Valid to" error={errors.valid_to?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('valid_to')} type="date" />}
            </Field>
          </div>

          <Field
            label="Sequence"
            error={errors.sequence?.message}
            hint="Lower runs first when more than one rule matches the same sale."
          >
            {(props) => (
              <Input {...props} {...register('sequence')} inputMode="numeric" autoComplete="off" />
            )}
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox {...register('is_active')} />
            Active — an inactive rule is kept on record but never applied.
          </label>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
